import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@marine/ui/dialog';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { RadioGroup, RadioGroupItem } from '@marine/ui/radio-group';
import { useCreateCustomer } from '@/modules/crm/hooks/useCustomers';
import { quickCustomerSchema, type QuickCustomerFormData } from '@/modules/crm/validators/customerSchema';
import { toast } from 'sonner';
import { getErrorMessage } from '@marine/utils/getErrorMessage';

interface QuickCreateCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (customerId: string) => void;  // Changed from number to string for ULID
}

const quickCustomerDefaults: QuickCustomerFormData = {
  customer_type: 'individual',
  first_name: '',
  last_name: '',
  company_name: '',
  email: '',
  phone: '',
};

export function QuickCreateCustomerDialog({
  open,
  onOpenChange,
  onSuccess,
}: QuickCreateCustomerDialogProps) {
  const createCustomer = useCreateCustomer();

  const form = useForm<QuickCustomerFormData>({
    resolver: zodResolver(quickCustomerSchema),
    defaultValues: quickCustomerDefaults,
  });
  const customerType = form.watch('customer_type') ?? 'individual';

  useEffect(() => {
    if (!open) return;
    form.reset(quickCustomerDefaults);
  }, [open, form]);

  const onSubmit = async (data: QuickCustomerFormData) => {
    try {
      const normalizedEmail = data.email?.trim();
      const companyName =
        data.customer_type === 'business' ? data.company_name?.trim() || undefined : undefined;
      const customer = await createCustomer.mutateAsync({
        customer_type: data.customer_type,
        first_name: data.first_name,
        last_name: data.last_name,
        company_name: companyName,
        email: normalizedEmail || undefined,
        phone: data.phone || undefined,
        status: 'lead',
      });
      toast.success('Customer created successfully');
      onOpenChange(false);
      form.reset();
      
      // Wait a bit for the query cache to invalidate and refetch
      // so the customer appears in the combobox
      if (onSuccess && customer?.id) {
        setTimeout(() => {
          onSuccess(customer.id);  // Pass ULID string directly
        }, 100);
      }
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Failed to create customer'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Quick Create Customer</DialogTitle>
          <DialogDescription>
            Add a new customer quickly. You can add more details later.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(event) => {
            // 09-UAT gap 1 (defence in depth) — a submit event bubbles
            // through a Radix Dialog portal to any ancestor <form>. This
            // dialog already mounts outside every caller's <form>, but stop
            // propagation anyway so a future mount site can't resurrect the
            // double-submit bug.
            event.stopPropagation();
            void form.handleSubmit(onSubmit)(event);
          }}
          className="space-y-4"
        >
          {/* Customer Type */}
          <div className="space-y-2">
            <Label>Customer Type</Label>
            <RadioGroup
              value={customerType}
              onValueChange={(value) => {
                form.setValue('customer_type', value as 'individual' | 'business');
              }}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="individual" id="quick-individual" />
                <Label htmlFor="quick-individual" className="font-normal">Individual</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="business" id="quick-business" />
                <Label htmlFor="quick-business" className="font-normal">Business</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">
                First Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="first_name"
                {...form.register('first_name')}
                placeholder="John"
                autoFocus
              />
              {form.formState.errors.first_name && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.first_name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="last_name">
                Last Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="last_name"
                {...form.register('last_name')}
                placeholder="Doe"
              />
              {form.formState.errors.last_name && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.last_name.message}
                </p>
              )}
            </div>
          </div>

          {/* Company Name (conditional) */}
          {customerType === 'business' && (
            <div className="space-y-2">
              <Label htmlFor="company_name">
                Company Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="company_name"
                {...form.register('company_name')}
                placeholder="Acme Corp"
              />
              {form.formState.errors.company_name && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.company_name.message}
                </p>
              )}
            </div>
          )}

          {/* Contact Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                {...form.register('email')}
                placeholder="john@example.com"
              />
              {form.formState.errors.email && (
                <p className="text-xs text-destructive">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                {...form.register('phone')}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createCustomer.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createCustomer.isPending}>
              {createCustomer.isPending ? 'Creating...' : 'Create Customer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
